/**
 * F6 — Progress overview + media-key rework (v2).
 * - media key parsed on "|" (pipe) not "_" (fixes multi-word stage name bug per §B)
 * - status-name dropdown resolves stageId→label via byId (data-driven)
 * - avgWbsPercent from server progressPercentCompletion UNCHANGED (§F)
 */
import React, { useState, useEffect,  } from "react";


import { useUser } from "../../context/UserContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-number-input/style.css";
import {  PackageCheck,  } from "lucide-react";
import {

  PrimaryButton,
} from "../../components/Buttons";
import Select from "react-select";
import usePrivilege from "../../hooks/usePrivilege";
import axiosInstance from "../../services/api/axios";
import ProjectStatus from "./ProjectStatus";
import BuildingStatus from "./BuildingStatus";
import ImageCarousel from "../ImageCarousel";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // Import CSS for carousel
import OccupancyLetterUpload from "../OccupancyLetterUpload";
import SummaryCard from "../SummaryCard";

import { useParams } from "react-router-dom";
import ConstructionPlan from "./ConstructionPlan";
import { useWbsStages } from "../../constants/wbsStages";

const customStyles = {
  menu: (provided, state) => ({
    ...provided,
    zIndex: 9999,
    width: "400px",
    maxHeight: "200px",
    overflowY: "auto",
    ...(state.placement === "top" ? { bottom: "auto", marginTop: "2px" } : { top: "auto", marginBottom: "9px" }),
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    borderRadius: "4px",
    border: "1px solid #ccc",
    backgroundColor: "white",
  }),
  control: (provided, state) => ({
    ...provided,
    zIndex: 9999,
    backgroundColor: "white",
    border: "1px solid #ccc",
    borderRadius: "4px",
    boxShadow: state.isFocused ? "0 0 0 1px gray" : null,
    "&:hover": {
      borderColor: state.isFocused ? "gray" : "#aaa",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    padding: "8px 12px",
    fontSize: "14px",
    color: state.isSelected ? "white" : "black",
    backgroundColor: state.isSelected ? "gray" : "white",
    "&:hover": {
      backgroundColor: state.isSelected ? "gray" : "#f0f0f0",
      cursor: "pointer",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "14px",
    color: "black",
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: "14px",
    color: "#777",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#aaa",
    "&:hover": {
      color: "#333",
    },
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: "none",
  }),
};

function ProgressStatus({projectId,phaseId}) {
  // v2: catalog from /wbs/stages (for media-key label resolution)
  const { byId: stageById } = useWbsStages();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [newImages, setNewImages] = useState([]); // State for base64 new images
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mediaImages, setMediaImages] = useState([]);
  const [projectStatus, setProjectStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = useUser();
  const { userInfo } = useUser();
  // eslint-disable-next-line no-unused-vars
  const role = userInfo?.persona || "Guest";
  const { hasPrivilege } = usePrivilege();

  const [activeTab, setActiveTab] = useState("ConstructionPlan"); // Default to 'project' tab
  const [filterCriteria, setFilterCriteria] = useState("all"); // Default filter
  const [buildingOptions, setBuildingOptions] = useState([]);
  const [statusNameOptions, setStatusNameOptions] = useState([]);

  const [activeBuildingId, setActiveBuildingId] = useState("all"); // Default filter
  const [activeStatusName, setActiveStatusName] = useState("all"); // Default filter
  const [buildingNameMap, setBuildingNameMap] = useState({}); // Map to store building names
  const [buildingMetadata, setBuildingMetadata] = useState([]);
  const [avgWbsPercent, setAvgWbsPercent] = useState(0);

  const fetchImages = async () => {
    try {
      const response = await axiosInstance.get("/projects/phase/media", {
        params: {
          // Backend Query alias is camelCase (projectPhaseId) — snake_case 422s
          projectPhaseId: phaseId,
          progress: true,
        },
      });
      setMediaImages(response);
    } catch (error) {
      console.error("Error fetching media images:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch images from the API
  useEffect(() => {
    if (projectId) {
      fetchImages();
    }
  }, [projectId]);

  // Function to fetch building name
  const fetchBuildingName = async (buildingId) => {
    try {
      const response = await axiosInstance.get(
        `/projects/buildings?buildingId=${buildingId}`
      );
   
      return `${response[0].buildingName}`;
    } catch (error) {
      console.error(
        `Error fetching building name for ID ${buildingId}:`,
        error
      );
      return `${buildingId}`;
    }
  };

  // Parse options and fetch building names when images load
  useEffect(() => {
    if (mediaImages && mediaImages.length > 0) {
      // Extract unique buildingIds and statusNames from the keys
      const uniqueBuildingIds = new Set();
      const uniqueStatusNames = new Set();
      console.log(mediaImages);
      mediaImages.forEach((image) => {
        if (image.key) {
          // v2: mediaKey format is "eventId|stageId|floor" (pipe delimiter, NOT underscore)
          // This fixes the multi-word stage name parse bug (§B contract §F6)
          const parts = image.key.split("|");
          if (parts.length >= 3) {
            uniqueBuildingIds.add(parts[0]); // eventId (used as building grouping key)
            const stageId = parseInt(parts[1], 10);
            // v2: resolve stage label from catalog (data-driven, not literal)
            const stageLabel = stageById[stageId]?.label || parts[1];
            uniqueStatusNames.add(stageLabel);
          }
        }
      });

      const buildingIdArray = Array.from(uniqueBuildingIds);

      // Fetch building names for all unique building IDs
      const fetchBuildingNames = async () => {
        const buildingNamePromises = buildingIdArray.map((buildingId) =>
          fetchBuildingName(buildingId)
        );
        const buildingNames = await Promise.all(buildingNamePromises);

        // Create a map of buildingId to buildingName
        const buildingMap = {};
        buildingIdArray.forEach((buildingId, index) => {
          buildingMap[buildingId] = buildingNames[index];
        });

        setBuildingNameMap(buildingMap);
        setBuildingOptions(buildingIdArray); // Set building options after building names are fetched
      };

      fetchBuildingNames(); // Call the function to fetch building names

      // Convert sets to arrays for dropdown options

      setStatusNameOptions(Array.from(uniqueStatusNames));
    }
  }, [mediaImages]);

  const handleFileChange = async (event) => {
    event.stopPropagation();
    const files = Array.from(event.target.files);
    const validImages = files.filter((file) => file.type.startsWith("image/"));

    // Convert valid images to base64 and store in newImages state
    const base64Promises = validImages.map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve({ name: file.name, base64: event.target.result });
        };
        reader.onerror = (error) => {
          reject(error);
        };
        reader.readAsDataURL(file);
      });
    });

    try {
      const base64Images = await Promise.all(base64Promises);
      setNewImages((prevImages) => [...prevImages, ...base64Images]);
      setSelectedFiles((prevFiles) => [...prevFiles, ...validImages]); // Still store File objects for upload
    } catch (error) {
      console.error("Error converting images to base64:", error);
      toast.error("Failed to process images. Please try again.");
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const validImages = files.filter((file) => file.type.startsWith("image/"));

    const base64Promises = validImages.map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve({ name: file.name, base64: event.target.result });
        };
        reader.onerror = (error) => {
          reject(error);
        };
        reader.readAsDataURL(file);
      });
    });

    try {
      const base64Images = await Promise.all(base64Promises);
      setNewImages((prevImages) => [...prevImages, ...base64Images]);
      setSelectedFiles((prevFiles) => [...prevFiles, ...validImages]);
    } catch (error) {
      console.error("Error converting images to base64:", error);
      toast.error("Failed to process images. Please try again.");
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const removeImage = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    setNewImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };
  const handleImageRemove = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    setNewImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append(
      "customer_id",
      user.userInfo.loginIdentifierDetails.customer.customerId
    );
    formData.append("project_id", projectId);
    selectedFiles.forEach((file) => formData.append("images", file));

    setIsSubmitting(true);

    try {
      const response = await axiosInstance.post(
        "/projects/phase/progress-media",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("Images uploaded successfully!");
      setSelectedFiles([]);
      setNewImages([]); // Clear base64 images after successful upload
      setIsModalOpen(false);
      await fetchImages(); // Fetch the images here
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload images. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter Images function
  const filterImages = () => {
    if (
      activeBuildingId === "all" &&
      activeStatusName === "all" &&
      mediaImages &&
      mediaImages.length > 0
    ) {
      return mediaImages;
    }
    if (mediaImages && mediaImages.length > 0) {
      return mediaImages.filter((image) => {
        if (image.key) {
          // v2: pipe-delimited mediaKey "eventId|stageId|floor"
          const parts = image.key.split("|");
          const buildingId = parts[0]; // eventId used as building grouping
          const stageId = parseInt(parts[1], 10);
          const stageLabel = stageById[stageId]?.label || parts[1];

          const buildingMatch =
            activeBuildingId === "all" || buildingId === activeBuildingId;
          const statusNameMatch =
            activeStatusName === "all" || stageLabel === activeStatusName;

          return buildingMatch && statusNameMatch;
        }
        return false;
      });
    }
    return [];
  };
// API call for /building/metadata
const fetchBuildingMetadata = async () => {
  if (!phaseId) return;
  try {
    console.log("Fetching building metadata for phaseId:", phaseId);
    const response = await axiosInstance.get('/building/metadata', {
      params: { projectPhaseId: phaseId },
    });
    
    // Handle paginated response format
    let metadataArray = [];
    if (response?.items && Array.isArray(response.items)) {
      metadataArray = response.items;
    } else if (Array.isArray(response)) {
      metadataArray = response;
    }
    
    console.log("Building metadata response for phaseId", phaseId, ":", metadataArray);
    setBuildingMetadata(metadataArray);
  } catch (error) {
    console.error("Error fetching building metadata for phaseId", phaseId, ":", error);
    toast.error("Failed to fetch building metadata.");
    setBuildingMetadata([]);
  }
};
useEffect(() => {
  fetchBuildingMetadata();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [phaseId]);
  const filteredImages = filterImages();

  const handleBuildingChange = (e) => {
    e.stopPropagation();
    setActiveBuildingId(e.target.value);
  };

  const handleStatusNameChange = (e) => {
    e.stopPropagation();
    setActiveStatusName(e.target.value);
  };

  const getBuildingName = (buildingId) => {
    return buildingNameMap[buildingId] || ` ${buildingId}`;
  };



  // Calculate average project percent completion
  useEffect(() => {
    const calculateAvgProgressCompletion = () => {
      if (buildingMetadata && buildingMetadata.length > 0) {
        let totalProgressPercentCompletion = 0;
        console.log("Calculating completion for phaseId:", phaseId);
        console.log("Building metadata:", buildingMetadata);
        buildingMetadata.forEach((building) => {
          const buildingProgress = building?.progressPercentCompletion || 0;
          console.log(`Building ${building.buildingId || 'unknown'}: ${buildingProgress}%`);
          totalProgressPercentCompletion += buildingProgress;
        });
        const avgCompletion = totalProgressPercentCompletion / buildingMetadata.length;
        console.log(`Total: ${totalProgressPercentCompletion}, Count: ${buildingMetadata.length}, Average: ${avgCompletion.toFixed(2)}%`);
        setAvgWbsPercent(avgCompletion);
      } else {
        console.log("No building metadata for phaseId:", phaseId, "- setting to 0%");
        setAvgWbsPercent(0);
      }
    };

    calculateAvgProgressCompletion();
  }, [buildingMetadata, phaseId]);

  return (
    <div className="flex h-full gap-4 p-2">
     

      {/* Right Side Content */}
      <div className="flex-1 p-2">
      
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Progress Images Section */}
              <div className="p-6 bg-white rounded-lg shadow-sm col-span-2">
                <div className="flex justify-between items-center mb-4">
                  <h1 className="font-medium text-lg">Phase Progress Images</h1>
                  {/* {hasPrivilege("canEdit") && (
                      <PrimaryButton onClick={() => setIsModalOpen(true)}>
                        Upload Images
                      </PrimaryButton>
                    )} */}
                </div>

                {/* Filter Dropdowns */}
                <div className="flex space-x-4 mb-4">
                  {/* Building Filter */}
                  <div className="flex-1">
                    <label
                      htmlFor="buildingFilter"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                     Building
                    </label>
                    <Select
                      value={{ value: activeBuildingId, label: activeBuildingId === "all" ? "All Buildings" : getBuildingName(activeBuildingId) }}
                      onChange={(option) => handleBuildingChange({ target: { value: option.value } })}
                      options={[
                        { value: "all", label: "All Buildings" },
                        ...buildingOptions.map((buildingId) => ({
                          value: buildingId,
                          label: getBuildingName(buildingId),
                        })),
                      ]}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      placeholder="Select Building to Filter"
                      className="text-sm"
                      styles={customStyles}
                    />
                  </div>
                  {/* Status Name Filter */}
                  <div className="flex-1">
                    <label
                      htmlFor="statusNameFilter"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Status:
                    </label>
                    <Select
                      value={{ value: activeStatusName, label: activeStatusName === "all" ? "All Statuses" : activeStatusName }}
                      onChange={(option) => handleStatusNameChange({ target: { value: option.value } })}
                      options={[
                        { value: "all", label: "All Statuses" },
                        ...statusNameOptions.map((statusName) => ({
                          value: statusName,
                          label: statusName,  
                        })),
                      ]}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.value}
                      placeholder="Select Status to Filter"
                      className="text-sm"
                      styles={customStyles}
                    />
                  </div>
                </div>

                {/* Image Carousel */}
                <div>
                  {loading ? (
                    <div className="flex justify-center items-center">
                      <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-t-transparent border-blue-500" />
                    </div>
                  ) : (
                    <ImageCarousel
                      images={filteredImages}
                      newImages={newImages}
                      isEditing={selectedFiles.length > 0}
                      handleImageRemove={handleImageRemove}
                      isProgressSection={true} // Flag for progress section
                    />
                  )}
                </div>
              </div>
              
              {/* column with the properties and occupacy stacked vertically */}
              <div className="col-span-1 flex flex-col space-y-4">
                <div className="">
                  <SummaryCard
                    icon={<PackageCheck size={24} />}
                    title={"Phase Completion % "}
                    value={`${avgWbsPercent.toFixed(2)}%`} // Display calculated percentage
                  />
                </div>

                {hasPrivilege("canEdit") && (
                  <div className="p-4 bg-white rounded-lg shadow-md col-span-1  border border-gray-50">
                    <OccupancyLetterUpload projectId={projectId} phaseId={phaseId} />
                  </div>
                )}
              </div>
            </div>
          </div>
        

       

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-11/12 max-w-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">Upload Progress Images</h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-gray-800"
                >
                  ✖
                </button>
              </div>

              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors mb-4"
              >
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="text-blue-600 underline cursor-pointer"
                >
                  Click to browse files
                </label>
                <p className="text-gray-500 mt-2">
                  or drag and drop images here
                </p>
              </div>

              {selectedFiles.length > 0 && (
                <div>
                  <h3 className="text-md font-semibold mb-2">Preview Images:</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="relative">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="w-full h-24 object-cover rounded shadow-lg"
                        />
                        <button
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 text-xs"
                        >
                          ✖
                        </button>
                        <p className="text-sm text-gray-700 mt-1">{file.key}</p>{" "}
                        {/* Display the file name */}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <PrimaryButton onClick={handleSubmit} disabled={isSubmitting}>
                  {isSubmitting ? "Uploading..." : "Upload"}
                </PrimaryButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProgressStatus;